import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Bug, Loader2, Upload, AlertCircle, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useFileUpload, formatBytes } from "@/hooks/use-file-upload";

interface ReportBugDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const ReportBugDialog = ({ open, onOpenChange }: ReportBugDialogProps) => {
    const { t } = useTranslation();
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [stepsToReproduce, setStepsToReproduce] = useState("");
    const [expectedBehavior, setExpectedBehavior] = useState("");
    const [actualBehavior, setActualBehavior] = useState("");

    const maxSize = 5 * 1024 * 1024; // 5MB
    const maxFiles = 5;

    const [
        { files, isDragging, errors },
        {
            handleDragEnter,
            handleDragLeave,
            handleDragOver,
            handleDrop,
            openFileDialog,
            removeFile,
            clearFiles,
            getInputProps,
        },
    ] = useFileUpload({
        accept: "image/*",
        maxFiles,
        maxSize,
        multiple: true,
    });

    const reportBugMutation = trpc.bugs.reportBug.useMutation({
        onSuccess: () => {
            toast.success(t("bugReport.successMessage"));
            // Reset form
            setTitle("");
            setDescription("");
            setStepsToReproduce("");
            setExpectedBehavior("");
            setActualBehavior("");
            clearFiles();
            onOpenChange(false);
        },
        onError: (error) => {
            toast.error(t("bugReport.errorMessage") + ": " + error.message);
        },
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!title.trim()) {
            toast.error(t("bugReport.validation.titleRequired"));
            return;
        }

        if (!description.trim()) {
            toast.error(t("bugReport.validation.descriptionRequired"));
            return;
        }

        // Convert images to base64
        const imageDataPromises = files.map(fileWithPreview => {
            return new Promise<string>((resolve, reject) => {
                if (fileWithPreview.file instanceof File) {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(fileWithPreview.file);
                } else {
                    reject(new Error("Invalid file type"));
                }
            });
        });

        try {
            const imageData = await Promise.all(imageDataPromises);

            reportBugMutation.mutate({
                title: title.trim(),
                description: description.trim(),
                stepsToReproduce: stepsToReproduce.trim() || undefined,
                expectedBehavior: expectedBehavior.trim() || undefined,
                actualBehavior: actualBehavior.trim() || undefined,
                screenshots: imageData.length > 0 ? imageData : undefined,
                includeLogs: true,
            });
        } catch (error) {
            toast.error(t("bugReport.errorMessage"));
        }
    };

    return (
        <Dialog onOpenChange={onOpenChange} open={open}>
            <DialogContent className="md:max-w-3xl overflow-hidden p-0">
                <div className="flex flex-col h-full">
                    {/* Header with gradient background */}
                    <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-secondary/10 px-6 py-5 border-b">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-3 text-xl">
                                <div className="p-2 rounded-lg bg-background shadow-sm">
                                    <Bug className="h-5 w-5 text-primary" />
                                </div>
                                {t("bugReport.title")}
                            </DialogTitle>
                            <DialogDescription className="mt-2">
                                {t("bugReport.description")}
                                <span className="block mt-2 text-xs text-muted-foreground">
                                    Logs are automatically collected for debugging purposes
                                </span>
                            </DialogDescription>
                        </DialogHeader>
                    </div>

                    {/* Form content with scroll */}
                    <div className="flex-1 overflow-y-auto max-h-[60vh] 2xl:max-h-[70vh] px-6 py-6">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Required fields section */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                    <div className="h-px flex-1 bg-border" />
                                    <span>Required Information</span>
                                    <div className="h-px flex-1 bg-border" />
                                </div>

                                {/* Title */}
                                <div className="space-y-2">
                                    <Label htmlFor="bug-title" className="text-sm font-medium">
                                        {t("bugReport.fields.title")} <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        id="bug-title"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder={t("bugReport.placeholders.title")}
                                        disabled={reportBugMutation.isPending}
                                        className="h-11"
                                    />
                                </div>

                                {/* Description */}
                                <div className="space-y-2">
                                    <Label htmlFor="bug-description" className="text-sm font-medium">
                                        {t("bugReport.fields.description")} <span className="text-red-500">*</span>
                                    </Label>
                                    <Textarea
                                        id="bug-description"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder={t("bugReport.placeholders.description")}
                                        rows={4}
                                        disabled={reportBugMutation.isPending}
                                        className="resize-none"
                                    />
                                </div>
                            </div>

                            {/* Optional details section */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                    <div className="h-px flex-1 bg-border" />
                                    <span>Additional Details (Optional)</span>
                                    <div className="h-px flex-1 bg-border" />
                                </div>

                                {/* Two column layout for optional fields */}
                                <div className="grid gap-4 sm:grid-cols-2">
                                    {/* Steps to Reproduce */}
                                    <div className="space-y-2">
                                        <Label htmlFor="bug-steps" className="text-sm font-medium">
                                            {t("bugReport.fields.stepsToReproduce")}
                                        </Label>
                                        <Textarea
                                            id="bug-steps"
                                            value={stepsToReproduce}
                                            onChange={(e) => setStepsToReproduce(e.target.value)}
                                            placeholder={t("bugReport.placeholders.stepsToReproduce")}
                                            rows={3}
                                            disabled={reportBugMutation.isPending}
                                            className="resize-none text-sm"
                                        />
                                    </div>

                                    {/* Expected Behavior */}
                                    <div className="space-y-2">
                                        <Label htmlFor="bug-expected" className="text-sm font-medium">
                                            {t("bugReport.fields.expectedBehavior")}
                                        </Label>
                                        <Textarea
                                            id="bug-expected"
                                            value={expectedBehavior}
                                            onChange={(e) => setExpectedBehavior(e.target.value)}
                                            placeholder={t("bugReport.placeholders.expectedBehavior")}
                                            rows={3}
                                            disabled={reportBugMutation.isPending}
                                            className="resize-none text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Actual Behavior - full width */}
                                <div className="space-y-2">
                                    <Label htmlFor="bug-actual" className="text-sm font-medium">
                                        {t("bugReport.fields.actualBehavior")}
                                    </Label>
                                    <Textarea
                                        id="bug-actual"
                                        value={actualBehavior}
                                        onChange={(e) => setActualBehavior(e.target.value)}
                                        placeholder={t("bugReport.placeholders.actualBehavior")}
                                        rows={3}
                                        disabled={reportBugMutation.isPending}
                                        className="resize-none text-sm"
                                    />
                                </div>
                            </div>

                            {/* Screenshots section */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                    <div className="h-px flex-1 bg-border" />
                                    <span>Screenshots (Optional, max 5)</span>
                                    <div className="h-px flex-1 bg-border" />
                                </div>

                                {/* Drop area */}
                                <div
                                    className="flex min-h-48 flex-col items-center not-data-[files]:justify-center rounded-xl border-2 border-dashed border-muted-foreground/25 p-6 transition-all has-[input:focus]:border-ring has-[input:focus]:ring-4 has-[input:focus]:ring-ring/10 data-[files]:hidden data-[dragging=true]:border-primary data-[dragging=true]:bg-primary/5"
                                    data-dragging={isDragging || undefined}
                                    data-files={files.length > 0 || undefined}
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                >
                                    <input
                                        {...getInputProps()}
                                        aria-label="Upload screenshots"
                                        className="sr-only"
                                    />
                                    <div className="flex flex-col items-center justify-center text-center">
                                        <div
                                            aria-hidden="true"
                                            className="mb-3 flex size-16 shrink-0 items-center justify-center rounded-full border-2 bg-muted/50"
                                        >
                                            <Upload className="size-7 text-muted-foreground" />
                                        </div>
                                        <p className="mb-2 font-semibold text-sm">
                                            Drag and drop or click to upload
                                        </p>
                                        <p className="mb-4 text-muted-foreground text-xs">
                                            PNG, JPG up to {formatBytes(maxSize)} each • Maximum {maxFiles} files
                                        </p>
                                        <Button
                                            type="button"
                                            onClick={openFileDialog}
                                            variant="outline"
                                            disabled={reportBugMutation.isPending}
                                            className="h-10"
                                        >
                                            <Upload className="mr-2 size-4" />
                                            Select files
                                        </Button>
                                    </div>
                                </div>

                                {files.length > 0 && (
                                    <div className="space-y-3">
                                        {/* Table header */}
                                        <div className="flex items-center justify-between">
                                            <p className="font-medium text-sm">
                                                Uploaded files ({files.length}/{maxFiles})
                                            </p>
                                            <div className="flex gap-2">
                                                <Button
                                                    type="button"
                                                    onClick={openFileDialog}
                                                    size="sm"
                                                    variant="outline"
                                                    disabled={files.length >= maxFiles || reportBugMutation.isPending}
                                                    className="h-8"
                                                >
                                                    <Upload className="mr-1.5 size-3.5" />
                                                    Add more
                                                </Button>
                                                <Button
                                                    type="button"
                                                    onClick={clearFiles}
                                                    size="sm"
                                                    variant="outline"
                                                    disabled={reportBugMutation.isPending}
                                                    className="h-8"
                                                >
                                                    <Trash2 className="mr-1.5 size-3.5" />
                                                    Remove all
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Files table */}
                                        <div className="rounded-lg border">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow className="hover:bg-transparent">
                                                        <TableHead className="w-[50%] h-11">Name</TableHead>
                                                        <TableHead className="h-11">Size</TableHead>
                                                        <TableHead className="text-right h-11">Action</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {files.map((file) => (
                                                        <TableRow key={file.id}>
                                                            <TableCell className="font-medium">
                                                                <div className="flex items-center gap-3">
                                                                    {file.preview && (
                                                                        <img
                                                                            src={file.preview}
                                                                            alt={file.file instanceof File ? file.file.name : file.file.name}
                                                                            className="size-10 rounded-md object-cover ring-1 ring-border"
                                                                        />
                                                                    )}
                                                                    <span className="truncate text-sm">
                                                                        {file.file instanceof File ? file.file.name : file.file.name}
                                                                    </span>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="text-muted-foreground text-sm">
                                                                {formatBytes(file.file instanceof File ? file.file.size : file.file.size)}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                <Button
                                                                    type="button"
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    onClick={() => removeFile(file.id)}
                                                                    aria-label="Remove file"
                                                                    disabled={reportBugMutation.isPending}
                                                                    className="size-8 hover:bg-destructive/10 hover:text-destructive"
                                                                >
                                                                    <X className="size-4" />
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </div>
                                )}

                                {errors.length > 0 && (
                                    <div
                                        className="flex items-center gap-2 rounded-lg bg-destructive/10 p-3 text-destructive text-sm"
                                        role="alert"
                                    >
                                        <AlertCircle className="size-4 shrink-0" />
                                        <span>{errors[0]}</span>
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>

                    {/* Footer with actions - sticky at bottom */}
                    <div className="border-t bg-muted/30 px-6 py-4">
                        <div className="flex justify-end gap-3">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={reportBugMutation.isPending}
                                className="h-10"
                            >
                                {t("common.cancel")}
                            </Button>
                            <Button
                                type="submit"
                                disabled={reportBugMutation.isPending}
                                className="h-10"
                                onClick={handleSubmit}
                            >
                                {reportBugMutation.isPending ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {t("bugReport.submitting")}
                                    </>
                                ) : (
                                    <>
                                        <Bug className="mr-2 h-4 w-4" />
                                        {t("bugReport.submit")}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ReportBugDialog;
